import { Timestamp } from '@ps/functions/firebase/firestore';
import type { CommunityChannelMessage } from '@ps/models/lib/community-channel';

/**
 * The shared model timestamp type (`@ps/models` `AppDate` — a Firestore
 * `Timestamp`). Named off a canonical field rather than re-declared, since the
 * `AppDate` alias has no clean package subpath.
 */
export type AppDate = NonNullable<CommunityChannelMessage['pinnedAt']>;

/**
 * Convert a search-API ISO string timestamp into an {@link AppDate} (Firestore
 * `Timestamp`), so a message from the REST endpoint lands on the SAME canonical
 * shape as one from the Firestore listener (which already carries `Timestamp`s).
 * The single place strings become timestamps on the message ingestion path.
 */
export function toAppDate(value: string): AppDate;
export function toAppDate(
  value: string | null | undefined,
): AppDate | undefined;
export function toAppDate(
  value: string | null | undefined,
): AppDate | undefined {
  return value == null ? undefined : Timestamp.fromDate(new Date(value));
}

/**
 * Convert an {@link AppDate} (or an already-ISO string) to an ISO string, for
 * the view types that still carry string timestamps (channels, bookmarks).
 */
export function appDateToIso(
  value: AppDate | string | null | undefined,
): string | undefined {
  if (value == null) return undefined;
  return typeof value === 'string' ? value : value.toDate().toISOString();
}

/**
 * Normalize an {@link AppDate} (or ISO string) to a JS `Date` for formatting.
 * Falls back to now on an unparseable value so a bad timestamp never throws.
 */
export function appDateToDate(value: AppDate | string): Date {
  const date = typeof value === 'string' ? new Date(value) : value.toDate();
  return isNaN(date.getTime()) ? new Date() : date;
}
